/**
 * Collaboration configuration.
 *
 * The `COLLABORATION_SERVICE_URL` can be overridden via the DotenvWebpack
 * plugin from the `.env` file or via environment variables. When not set,
 * the URL is derived from `window.location` so that it automatically
 * points to the Nginx `/collaboration/` proxy in production.
 *
 * Authentication is handled end-to-end via the browser's Django `sessionid`
 * cookie: nginx serves the collaboration service (`/collaboration/`) and
 * cloud-login (`/api/*`, `/auth/*`) on the same origin, so the cookie flows
 * automatically on the WebSocket upgrade. No client-side token handling.
 */

/**
 * Build the default production WebSocket URL from the current page origin.
 * In production, an Nginx reverse-proxy serves the collaboration service
 * at the `/collaboration/` path on the same host.
 */
const getDefaultCollaborationUrl = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  return `${protocol}//${window.location.host}/collaboration/`;
};

/**
 * The WebSocket URL for the Collaboration Service.
 * Uses the env var when set (development), otherwise derives from window.location (production).
 */
export const getCollaborationServiceUrl = (): string => {
  return process.env.COLLABORATION_SERVICE_URL || getDefaultCollaborationUrl();
};

/**
 * Check whether the Collaboration Service URL is available.
 *
 * @returns true if a collaboration URL is available
 */
export const isCollaborationConfigured = (): boolean =>
  Boolean(getCollaborationServiceUrl());
